import React, { useState } from 'react';
import { ArrowLeft, Lightbulb, FolderPlus, PenTool, Layout, Component, FileImage } from 'lucide-react';

const GettingStartedArticle = ({ onBack }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="max-w-4xl mx-auto p-6">
      <button 
        onClick={onBack}
        className="flex items-center text-gray-600 hover:text-black mb-6"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Help Center
      </button>

      <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-6 rounded-lg mb-8">
        <div className="flex items-start">
          <Lightbulb className="w-6 h-6 text-yellow-500 mr-3 flex-shrink-0 mt-1" />
          <div>
            <h1 className="text-2xl font-bold mb-3">Getting Started with Prototyping</h1>
            <p className="text-gray-700">
              Welcome to our AI-powered design tool! This guide will walk you through creating your first project, 
              understanding the interface, and generating your first prototype. In just a few minutes, 
              you'll learn how to transform your ideas into interactive designs using our three flexible design methods.
            </p>
          </div>
        </div>
      </div>

      {!expanded ? (
        <div>
          <p className="text-gray-700 mb-6">
            Our platform simplifies the design process, allowing anyone to create professional-looking 
            prototypes without specialized design skills. By combining your ideas with AI-powered generation, 
            you can quickly create, revise, and share your designs using structured components, freestyle descriptions,
            or by uploading wireframes.
          </p>
          <button
            onClick={() => setExpanded(true)}
            className="text-black font-medium hover:underline"
          >
            Read more
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          <section>
            <h2 className="text-xl font-semibold flex items-center mb-4">
              <FolderPlus className="w-5 h-5 mr-2 text-gray-700" />
              Creating Your First Project
            </h2>
            <ol className="list-decimal pl-5 space-y-4 text-gray-700">
              <li>
                <strong>Sign up or log in</strong> to your account to access the dashboard.
              </li>
              <li>
                <strong>Click "New Project"</strong> on the dashboard to start creating.
              </li>
              <li>
                <strong>Fill in the project details</strong>:
                <ul className="list-disc pl-5 mt-2 space-y-1">
                  <li><strong>Title</strong>: Choose a clear name for your project.</li>
                  <li><strong>Description</strong>: Describe what you're building and its purpose.</li>
                  <li><strong>User Profile</strong>: Outline who will use this product (age, technical skill, etc.).</li>
                </ul>
              </li>
              <li>
                <strong>Select your design preferences</strong> including device type (mobile or desktop), color theme, and font family.
              </li>
              <li>
                <strong>Click "Save and Continue"</strong> to move to the project workspace.
              </li>
            </ol>
          </section>

          <section>
            <h2 className="text-xl font-semibold flex items-center mb-4">
              <PenTool className="w-5 h-5 mr-2 text-gray-700" />
              Creating Your First View
            </h2>
            <div className="text-gray-700">
              <p className="mb-4">
                Views are individual screens or pages in your design. You have three different ways to create views:
              </p>
              
              <div className="mb-6">
                <h3 className="font-semibold mb-2 flex items-center">
                  <Component className="w-4 h-4 mr-2 text-gray-700" />
                  1. Structured Method
                </h3>
                <p className="mb-2">
                  This approach lets you build your view by adding sections and UI elements in a structured way:
                </p>
                <ol className="list-decimal pl-5 space-y-2">
                  <li>Choose layout type (single-page, left sidebar, right sidebar, split-screen)</li>
                  <li>Add optional panels (top panel, bottom panel)</li>
                  <li>Add UI elements to each section (header, cards, buttons, etc.)</li>
                  <li>Describe interactivity and specific content requirements</li>
                </ol>
                <p className="mt-2 text-gray-600 text-sm">
                  Best for: Complex layouts with multiple sections when you want precise control over UI structure.
                </p>
              </div>
              
              <div className="mb-6">
                <h3 className="font-semibold mb-2 flex items-center">
                  <PenTool className="w-4 h-4 mr-2 text-gray-700" />
                  2. Freestyle Method
                </h3>
                <p className="mb-2">
                  Write a free-form description of your desired user interface:
                </p>
                <ol className="list-decimal pl-5 space-y-2">
                  <li>Enter a title for your view</li>
                  <li>Write detailed instructions describing your desired UI</li>
                  <li>Include details about layout, components, functionality, and content</li>
                </ol>
                <p className="mt-2 text-gray-600 text-sm">
                  Best for: Simple interfaces or when you have a clear mental image you want to describe in your own words.
                </p>
              </div>
              
              <div className="mb-6">
                <h3 className="font-semibold mb-2 flex items-center">
                  <FileImage className="w-4 h-4 mr-2 text-gray-700" />
                  3. Upload Wireframe Method
                </h3>
                <p className="mb-2">
                  Upload a wireframe, mockup, or screenshot and have AI analyze it:
                </p>
                <ol className="list-decimal pl-5 space-y-2">
                  <li>Enter a title for your view</li>
                  <li>Upload an image of your wireframe (JPEG, PNG, GIF, WEBP)</li>
                  <li>Review and edit the auto-generated UI description</li>
                </ol>
                <p className="mt-2 text-gray-600 text-sm">
                  Best for: When you have sketches, design mockups, or want to recreate an existing UI.
                </p>
              </div>
              
              <div className="mt-4">
                <h3 className="font-semibold mb-2">After Describing Your View:</h3>
                <ol className="list-decimal pl-5 space-y-2">
                  <li>
                    <strong>Click "Next Step"</strong> to proceed to AI clarification questions.
                  </li>
                  <li>
                    <strong>Answer the clarification questions</strong> to provide additional details about your design.
                  </li>
                  <li>
                    <strong>Click "Create View"</strong> and wait while the AI generates your design.
                  </li>
                </ol>
              </div>
              
              <div className="mt-4">
                <h3 className="font-semibold mb-2">Optional: Add a Reference</h3>
                <p>
                  For any method, you can optionally provide a reference to an existing view to help guide the AI's understanding:
                </p>
                <ul className="list-disc pl-5 mt-2">
                  <li>Select an existing view from the project</li>
                  <li>Add instructions on how the reference should be used</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold flex items-center mb-4">
              <Layout className="w-5 h-5 mr-2 text-gray-700" />
              Understanding the Interface
            </h2>
            <div className="text-gray-700">
              <p className="mb-4">
                Once your view is generated, you'll be able to:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li><strong>Preview the design</strong> in the main viewing area.</li>
                <li><strong>Download the HTML</strong> using the download button.</li>
                <li><strong>Revise the design</strong> by clicking the "Revise this version" button and providing specific changes.</li>
                <li><strong>Add image assets</strong> by uploading images to replace placeholders.</li>
                <li><strong>Add video embeds</strong> by providing video URLs for video placeholders.</li>
                <li><strong>View different versions</strong> using the navigation arrows if you've made multiple revisions.</li>
                <li><strong>Return to project dashboard</strong> by clicking the close button.</li>
              </ul>
              <p className="mt-4">
                Each project can contain multiple views, and you can organize these views into flows to create a complete user journey.
              </p>
            </div>
          </section>

          <section className="bg-gray-50 p-6 rounded-lg">
            <h2 className="text-xl font-semibold mb-4">Tips for Success</h2>
            <ul className="list-disc pl-5 space-y-2 text-gray-700">
              <li><strong>Choose the right input method</strong> for your needs:
                <ul className="list-disc pl-5 mt-1 text-sm">
                  <li><em>Structured</em> for complex, multi-section layouts</li>
                  <li><em>Freestyle</em> for simple views or natural language descriptions</li>
                  <li><em>Upload Wireframe</em> when you have existing designs or sketches</li>
                </ul>
              </li>
              <li><strong>Be detailed in your descriptions</strong> to get more accurate results.</li>
              <li><strong>Answer clarification questions thoroughly</strong> as they improve the final output.</li>
              <li><strong>Start with a simple view</strong> before building more complex ones.</li>
              <li><strong>Use "Revise" multiple times</strong> to iteratively improve a design rather than starting over.</li>
              <li><strong>Provide references</strong> when you have similar views that can guide the AI.</li>
              <li><strong>Organize related views into flows</strong> to simulate user journeys.</li>
            </ul>
          </section>

          <button
            onClick={() => setExpanded(false)}
            className="text-black font-medium hover:underline mt-4 block"
          >
            Show less
          </button>
        </div>
      )}
    </div>
  );
};

export default GettingStartedArticle;